"use client"

import React from "react"
import SectionTitle from "@/components/SectionTitle"
import HTMLRenderer from "@/components/ui/html-renderer"
// Two-column FAQ list with optional grouping

export type FAQ = { q: string; a: string }

interface FAQListProps {
  // If grouped is provided, it should be an object mapping category title -> array of FAQs.
  grouped?: Record<string, FAQ[]>
  // Fallback: a flat array of FAQs passed as 'faqs'
  faqs?: FAQ[]
  title?: string
  subtitle?: string
  className?: string
}

export default function FAQList({ grouped, faqs, title = 'Preguntas frecuentes', subtitle, className }: FAQListProps) {
  // Build groups: prefer grouped prop, otherwise a single 'General' group
  const groups = grouped ?? { General: faqs ?? [] }
  const categoryKeys = Object.keys(groups)

  const [activeCategory, setActiveCategory] = React.useState<string>(categoryKeys[0] ?? 'General')
  const items = groups[activeCategory] ?? []
  const [activeQuestion, setActiveQuestion] = React.useState<number | null>(null)

  React.useEffect(() => {
    // reset question when category changes
    setActiveQuestion(null)
  }, [activeCategory])

  return (
    <section className={`py-12 md:py-16 bg-white ${className ?? ''}`}>
      <div className="container mx-auto px-4 md:px-8 max-w-6xl">
        <div className="text-center mb-8">
          <SectionTitle text={title} variant="light" className="mb-2 text-4xl md:text-5xl" />
          {subtitle ? <p className="mx-auto max-w-3xl text-neutral-600">{subtitle}</p> : null}
        </div>

        <div className="grid grid-cols-12 gap-8">
          {/* Sidebar */}
          <nav className="col-span-3">
            <ul className="space-y-4">
              {categoryKeys.map((cat) => (
                <li key={cat}>
                  <button
                    onClick={() => setActiveCategory(cat)}
                    className={`w-full text-left px-4 py-3 rounded-md transition-colors ${cat === activeCategory ? 'bg-black text-white font-semibold' : 'text-gray-800 hover:bg-black hover:text-white'}`}
                  >
                    {cat}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* Right column: questions list with inline expandable answers */}
          <div className="col-span-9">
            <div className="mb-6 border-b border-gray-200">
              <ul className="flex flex-col gap-2">
                {items.map((f, i) => {
                  const open = activeQuestion === i
                  return (
                    <li key={i}>
                      <div className="py-2">
                        <button
                          onClick={() => setActiveQuestion(i === activeQuestion ? null : i)}
                          className={`w-full text-left py-3 flex items-center justify-between ${open ? 'text-gray-900' : 'text-gray-700'}`}
                        >
                          <span style={{ fontFamily: 'DIN, sans-serif' }} className="font-bold">{f.q}</span>
                          <span className="text-gray-400">{open ? '−' : '+'}</span>
                        </button>
                        <div
                          className="mt-3 text-gray-700 overflow-hidden"
                          style={{
                            maxHeight: open ? '1000px' : '0px',
                            whiteSpace: 'pre-line',
                            opacity: open ? 1 : 0,
                            transition: 'max-height 260ms ease, opacity 200ms ease',
                          }}
                        >
                          <div style={{ paddingRight: 10 }}>
                            <HTMLRenderer content={f.a} />
                          </div>
                        </div>
                      </div>
                      <hr className="border-gray-100" />
                    </li>
                  )
                })}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
